import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { App, Button, Form, Input, Select } from "antd";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { EProductPlanPaymentInterval } from "@devowl-wp/api-real-commerce";
import { copyToClipboard } from "@devowl-wp/react-utils";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../../../../../hooks/useRealCommerceErrorHandling.js";
import { trackAnalyticsEvent } from "../../../../../../others/tracking.js";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { AffiliatePanel } from "../affiliatePanel.js";
const INITIAL_VALUES = {
    code: "",
    products: []
};
const CreateCouponForm = observer(()=>{
    const { message } = App.useApp();
    const [isBusy, setIsBusy] = useState(false);
    const [form] = Form.useForm();
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const { accountV2Store: { session, isBusyAffiliateProducts, affiliateProducts } } = useStores();
    const affiliate = session?.affiliate;
    const onFinish = useCallback(async ({ code, products })=>{
        setIsBusy(true);
        try {
            await affiliate?.createCoupon({
                code,
                products: products.map((id)=>({
                        id
                    }))
            });
            trackAnalyticsEvent("affiliate_coupon_created", {
                ga: {
                    event_category: "account",
                    event_label: "Affiliate coupon created"
                },
                matomo: {
                    category: "account",
                    name: "Affiliate coupon created"
                }
            });
            // Copy the new code straight to the clipboard so it's ready to share.
            copyToClipboard(code);
            message.success(__("Your coupon is active and copied to your clipboard."));
            form.resetFields();
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    }, [
        affiliate
    ]);
    const productGroups = useMemo(()=>{
        const result = {};
        for (const product of affiliateProducts){
            const { defaultPaymentInterval } = product;
            const groupLabel = defaultPaymentInterval === EProductPlanPaymentInterval.OneTime ? __("One-time payment products") : __("Subscriptions");
            result[groupLabel] = result[groupLabel] || [];
            result[groupLabel].push(product);
        }
        return result;
    }, [
        affiliateProducts
    ]);
    return /*#__PURE__*/ _jsx(AffiliatePanel, {
        heading: __("Create a coupon code"),
        subline: __('Subscription coupons give 20 %% off in the first year. One-time-product coupons give 10 %% off. Code must be uppercase, no suggestive names like "BLACKFRIDAY".'),
        children: /*#__PURE__*/ _jsxs(Form, {
            form: form,
            onFinish: onFinish,
            initialValues: INITIAL_VALUES,
            layout: "vertical",
            disabled: isBusy,
            children: [
                /*#__PURE__*/ _jsx(Form.Item, {
                    label: __("Products"),
                    required: true,
                    name: "products",
                    rules: [
                        {
                            required: true,
                            message: __("Please choose at least one product!")
                        },
                        {
                            validator: async (rule, value)=>{
                                const uniqueSelectedIntervals = value.map((id)=>affiliateProducts.filter(({ id: i })=>i === id)[0]?.defaultPaymentInterval).filter((v, i, a)=>a.indexOf(v) === i);
                                if (uniqueSelectedIntervals.length > 1) {
                                    throw __("Pick either subscription products or one-time products - not both. They use different discount rules.");
                                }
                            }
                        }
                    ],
                    children: /*#__PURE__*/ _jsx(Select, {
                        mode: "multiple",
                        loading: isBusyAffiliateProducts,
                        showSearch: true,
                        optionFilterProp: "children",
                        children: Object.keys(productGroups).map((interval)=>/*#__PURE__*/ _jsx(Select.OptGroup, {
                                label: interval,
                                children: productGroups[interval].map(({ id, name })=>/*#__PURE__*/ _jsx(Select.Option, {
                                        value: id,
                                        children: name
                                    }, id))
                            }, interval))
                    })
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    label: __("Coupon code"),
                    required: true,
                    name: "code",
                    rules: [
                        {
                            required: true,
                            message: __("Please enter a coupon code!")
                        }
                    ],
                    children: /*#__PURE__*/ _jsx(Input, {
                        style: {
                            textTransform: "uppercase"
                        },
                        onChange: (e)=>form.setFieldValue("code", e.target.value.toUpperCase())
                    })
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    style: {
                        textAlign: "right",
                        marginBottom: 0
                    },
                    children: /*#__PURE__*/ _jsx(Button, {
                        type: "primary",
                        htmlType: "submit",
                        loading: isBusy,
                        children: __("Create coupon")
                    })
                })
            ]
        })
    });
});
export { CreateCouponForm };
